/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-21 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { SkeletonDisplayText, TextField } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import ProductItem from './product-item';

import { reorder } from 'src/helpers';

type Props = {
  index: number,
  updateField: Function,
  toggleModal: Function
};

type ComposedProps = WithI18nProps & Props;

const RuleItem = (props: ComposedProps): Component<ComposedProps> => {
  const { i18n, toggleModal, isLoading = false, updateField, products } = props;
  const renderProductList = () => {
    const value = _.cloneDeep(products);
    if (isLoading) {
      return (
        <div className="product__skeleton">
          <SkeletonDisplayText size="medium" />
        </div>
      );
    }

    if (_.isEmpty(value)) {
      return '';
    }

    return (
      <>
        <p className="field-helper" style={{ marginTop: '0.5rem' }}>
          {i18n.translate('AdminHTML.PredictiveBundle.Item.setting01_DragDrop_Helper')}
        </p>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div className="product-list">
                <ul ref={provided.innerRef} {...provided.droppableProps} className="content-list">
                  {value.map((product, index) => (
                    <Draggable
                      key={product.id ? product.id : index}
                      draggableId={`product-${product.id ? product.id : index}`}
                      index={index}>
                      {(provided, snapshot) => {
                        return (
                          <li
                            className="product-item"
                            ref={provided.innerRef}
                            {...provided.draggableProps}>
                            <ProductItem
                              product={product}
                              draggableProps={provided.draggableProps}
                              dragHandleProps={provided.dragHandleProps}
                              onRemove={onRemove}
                              productIndex={index}
                            />
                          </li>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </>
    );
  };

  const handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const reorderedProducts = reorder(products, result.source.index, result.destination.index);

    let listValue = _.cloneDeep(products);
    listValue = reorderedProducts;
    updateField('bundleProducts', listValue);
  };

  const onRemove = (fieldName, itemIndex) => {
    let listValue = _.cloneDeep(products);

    listValue.splice(itemIndex, 1);
    updateField('bundleProducts', listValue);
  };

  return (
    <div className="rule-group-block">
      <div className="main">
        <div className="header">
          {/* Currently, we have only one option to select */}
          <TextField
            autoComplete="off"
            onFocus={() => toggleModal()}
            placeholder={i18n.translate(
              'AdminHTML.PredictiveBundle.Item.setting01_SelectField_Placeholder'
            )}
            value=""
          />
        </div>
        <div className="body">{renderProductList()}</div>
      </div>
    </div>
  );
};

export default withI18n()(RuleItem);
